import * as React from "react";
import * as ReactDOM from "react-dom";
import { Route } from "react-router-dom";
import * as modelsGet from "../../../models/get";
import * as modelsPost from "../../../models/post";
import * as utils from "../../../utils/utils";
import * as apiPersons from "../../../api/persons";
import { Window } from "../../../components/window";
import { Loader, LoaderStyle } from "../../../components/loader";
import { Button, ButtonType } from "../../../components/button";
import { ScrollViewer, ScrollBarVisibility } from "../../../components/scrollViewer";
import { FormGroup } from "../../../components/form";
import { ComboBox, ComboBoxType, DROPDOWN_TEXT_ITEM_HEIGHT, DropDownTextItem } from "../../../components/comboBox";
import { InputMask, InputSize } from "../../../components/input";
import { InputValueType } from "../../../enumerations";
import { TextBox } from "../../../components/textBox";
import { Alert, AlertType } from "../../../components/alert";
import { CheckBox } from "../../../components/checkbox";

enum Mode
{
    create,
    edit
}

interface IProperties
{
    /**
     * Die ID des zu öffnenden Datensatzes. (Ist leer wenn eine neuer Datensatz erstellt werden soll.)
     */
    morticianId?: number,

    /**
     * Wird aufgerufen sobald sich das Fenster schließt.
     */
    onClose?: (refresh?: boolean) => void
}

interface IState
{
    /**
     * Gibt an, ob Daten gerade geladen werden.
     */
    isLoading?: boolean,

    /**
     * Gibt den Modus an in dem sich der Dialog befindet.
     */
    mode?: Mode,

    /**
     * Wenn im Bearbeitungsmodus, dann ist diese Eigenschaft gefüllt.
     */
    mortician?: modelsGet.IPerson,

    /**
     * Enthält die Eingabedaten des Forms.
     */
    form?: {
        title?: string,
        firstname?: string,
        lastname?: string,
        street?: string,
        streetNo?: string,
        country?: string,
        zipCode?: string,
        city?: string,
        isActive?: boolean
    },

    /**
     * Enthält Serverseitige Fehlermeldungen.
     */
    errors?: Object,

    /**
     * Der Text der Meldungen welche angezeigt werden soll.
     */
    alertMessage?: string,

    /**
     * Der Typ der Meldung.
     */
    alertType?: AlertType
}

export default class Dialog extends React.Component<IProperties, IState>
{
    private _initialState: IState;
    private _isSaveButtonDisabled: boolean;

    /**
     * Erstellt eine neue Instanz der Klasse @see MorticiansDialog.
     * @param props Die Eigenschaften dieser Komponente.
     */
    constructor(props: IProperties)
    {
        super(props);

        // Modus ermitteln.
        var mode: Mode = Mode.create;
        if (props.morticianId > 0)
        {
            mode = Mode.edit;
        }

        this.state = {
            isLoading: true,
            mode: mode,
            form: {},
            errors: {}
        };

        this.initAsync(props);
    }

    /**
     * Lädt alle benötigten Daten und/oder weißt diese zu.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initAsync(props: IProperties)
    {
        if (this.state.mode === Mode.edit)
        {
            await this.initEditModeAsync(props);
        }
        else if (this.state.mode === Mode.create)
        {
            await this.initCreateModeAsync(props);
        }
    }

    /**
     * Initialisert den Edit Mode.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initEditModeAsync(props: IProperties)
    {
        // Betreff aus der Datenbank laden.
        var mortician: modelsGet.IPerson = await apiPersons.getPersonById(props.morticianId);

        // Werte setzen.
        this.setState((state: IState) =>
        {
            state.isLoading = false;
            state.errors = {};
            state.alertMessage = null;
            state.alertType = null;
            state.mortician = mortician;

            state.form.title = mortician.title;
            state.form.firstname = mortician.firstname;
            state.form.lastname = mortician.lastname;
            state.form.street = mortician.street;
            state.form.streetNo = mortician.street_no;
            state.form.country = mortician.country;
            state.form.zipCode = mortician.zip_code;
            state.form.city = mortician.city;
            state.form.isActive = mortician.contractor_is_active;

            return state;
        }, () => this._initialState = {
            ...this.state,
            form: {
                ...this.state.form
            }
        });
    }

    /**
     * Initialisert den Create Mode.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initCreateModeAsync(props: IProperties)
    {
        // Irgend etwas muss geladen werden, sonst funktioniert setState nicht.
        var dummy = await apiPersons.getPersonById(0);

        // Neues State Objekt erstellen.
        this.setState(() =>
        {
            const newState: IState = {};
            newState.isLoading = false;
            newState.errors = {};
            newState.alertMessage = null;
            newState.alertType = null;

            newState.form = {};

            return newState;
        }, () => this._initialState = {
            ...this.state,
            form: {
                ...this.state.form
            }
        });
    }

    /**
     * Wird ausgeführt, bevor gerendert wird.
     * Hier wird geprüft ob der Button zum erstellen oder speichern
     * freigschaltet werden soll.
     * @param nextProps Die neuen Eigenschaften.
     * @param nextState Der neue State.
     */
    componentWillUpdate(nextProps: IProperties, nextState: IState)
    {
        this._isSaveButtonDisabled = true;

        if (this._initialState != null)
        {
            const { form } = this._initialState;

            // Anrede prüfen.
            if (form.title !== nextState.form.title)
                this._isSaveButtonDisabled = false;

            // Vorname prüfen.
            if (form.firstname !== nextState.form.firstname)
                this._isSaveButtonDisabled = false;

            // Nachname prüfen.
            if (form.lastname !== nextState.form.lastname)
                this._isSaveButtonDisabled = false;

            // Straße prüfen.
            if (form.street !== nextState.form.street)
                this._isSaveButtonDisabled = false;

            // Hausnummer prüfen.
            if (form.streetNo !== nextState.form.streetNo)
                this._isSaveButtonDisabled = false;

            // Land prüfen.
            if (form.country !== nextState.form.country)
                this._isSaveButtonDisabled = false;

            // PLZ prüfen.
            if (form.zipCode !== nextState.form.zipCode)
                this._isSaveButtonDisabled = false;

            // Ort prüfen.
            if (form.city !== nextState.form.city)
                this._isSaveButtonDisabled = false;

            // Aktivkennzeichen prüfen.
            if (form.isActive !== nextState.form.isActive)
                this._isSaveButtonDisabled = false;
        }
    }

    /**
     * Wird ausgeführt, bevor der Dialog sich schließt.
     */
    private onClosing = (refresh: boolean = false) =>
    {
        var closeDialog: boolean = true;

        if (!this._isSaveButtonDisabled)
            closeDialog = confirm("Es sind noch ungespeicherte Änderungen vorhanden. Wollen Sie den Dialog trotzdem schließen?");

        if (closeDialog)
        {
            const { onClose } = this.props;
            onClose && onClose(refresh);
            return true;
        }
    }

    /**
     * Ändert den geöffneten Datensatz oder erstellt einen neuen.
     */
    private onSaveOrCreateClick = async () =>
    {
        try
        {
            this.setState({
                isLoading: true
            });

            const { mode, mortician, form } = this.state;

            // Neues POST Objekt erstellen.
            var newData: modelsPost.IMortician = {};
            newData.title = form.title;
            newData.firstname = form.firstname;
            newData.lastname = form.lastname;
            newData.street = form.street;
            newData.street_no = form.streetNo;
            newData.country = form.country;
            newData.zip_code = form.zipCode;
            newData.city = form.city;
            newData.is_active = form.isActive;

            if (mode === Mode.edit)
            {
                await apiPersons.putMortician(mortician.id, newData);
            }
            else
            {
                await apiPersons.postMortician(newData);
            }

            // Fenster schließen.
            this._isSaveButtonDisabled = true;
            this.onClosing(true);
        }
        catch (e)
        {
            var errors = utils.getErrors(e);
            var alertMessage: string;

            if (errors != null)
            {
                for (let error in errors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                            alertMessage = errors[error];
                            break;
                        case "internal_server_error":
                            alertMessage = "Auf dem Server ist ein Fehler aufgetreten! Wenn der Fehler weiterhin auftritt, kontaktieren Sie Ihren Administrator oder Ihren Software Anbieter.";
                            break;
                    }
                }
            }

            this.setState(() =>
            {
                const newState: IState = {};
                newState.isLoading = false;
                newState.errors = errors;
                newState.alertMessage = alertMessage;
                newState.alertType = AlertType.error;
                return newState;
            });
        }
    }

    /**
     * Löscht den geöffneten Datensatz.
     */
    private onDeleteClick = async () =>
    {
        var deleteData = confirm("Wollen Sie den Datensatz wirklich löschen?");
        if (!deleteData)
            return;

        try
        {
            this.setState({
                isLoading: true
            });

            const { mortician } = this.state;

            await apiPersons.removeMortician(mortician.id);

            // Fenster schließen.
            this._isSaveButtonDisabled = true;
            this.onClosing(true);
        }
        catch (e)
        {
            var errors = utils.getErrors(e);
            var alertMessage: string;

            if (errors != null)
            {
                for (let error in errors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                            alertMessage = errors[error];
                            break;
                        case "internal_server_error":
                            alertMessage = "Auf dem Server ist ein Fehler aufgetreten! Wenn der Fehler weiterhin auftritt, kontaktieren Sie Ihren Administrator oder Ihren Software Anbieter.";
                            break;
                    }
                }
            }

            this.setState(() =>
            {
                const newState: IState = {};
                newState.isLoading = false;
                newState.errors = errors;
                newState.alertMessage = alertMessage;
                newState.alertType = AlertType.error;
                return newState;
            });
        }
    }

    /**
     * Wird ausgeführt, sobald sich der Wert eines Controls ändert.
     * @param value Der konvertierte Wert der TextBox.
     * @param originalValue Der originale Wert der TextBox.
     * @param name Der Name des Form Elements.
     */
    private onControlTextChanged = (value: any[], originalValue: string[], name: string) =>
    {
        this.setState((state: IState) =>
        {
            state.form[name] = value[0];
            return state;
        });
    }

    /**
     * Wird ausgeführt, sobald sich der Wert einer CheckBox ändert.
     * @param e Das auslösende Ereignis.
     */
    private onCheckboxChanged = (e: React.ChangeEvent<HTMLInputElement>) =>
    {
        var target = e.target;
        var value = target.checked;

        this.setState((state: IState) =>
        {
            state.form.isActive = value;
            return state;
        });
    }

    /**
     * Rendert das Formular/den Content Bereich.
     */
    private renderContent(): JSX.Element
    {
        const {
            mode,
            mortician,
            form,
            errors,
            alertMessage,
            alertType
        } = this.state;

        return (
            <div className="settings_dialog_content">
                <ScrollViewer
                    HorizontalScrollBarVisibility={ScrollBarVisibility.hidden}
                    VerticalScrollBarVisibility={ScrollBarVisibility.auto}
                    style={{
                        padding: 10
                    }}>
                    <FormGroup>
                        <TextBox
                            label="Anrede"
                            value={form.title}
                            size={InputSize.normal}
                            spellCheck={true}
                            errorMessage={errors["Title"]}
                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "title")} />
                    </FormGroup>
                    <FormGroup isHorizontal={true}>
                        <TextBox
                            label="Vorname"
                            value={form.firstname}
                            size={InputSize.normal}
                            spellCheck={true}
                            autoFocus={true}
                            errorMessage={errors["Firstname"]}
                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "firstname")} />
                        <TextBox
                            label="Nachname"
                            value={form.lastname}
                            size={InputSize.normal}
                            spellCheck={true}
                            errorMessage={errors["Lastname"]}
                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "lastname")} />
                    </FormGroup>
                    <FormGroup isHorizontal={true}>
                        <TextBox
                            label="Straße"
                            value={form.street}
                            size={InputSize.normal}
                            spellCheck={true}
                            errorMessage={errors["Street"]}
                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "street")} />
                        <TextBox
                            label="Hausnummer"
                            value={form.streetNo}
                            size={InputSize.normal}
                            errorMessage={errors["StreetNo"]}
                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "streetNo")} />
                    </FormGroup>
                    <FormGroup isHorizontal={true}>
                        <TextBox
                            label="Land"
                            value={form.country}
                            size={InputSize.normal}
                            spellCheck={true}
                            errorMessage={errors["Country"]}
                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "country")} />
                        <TextBox
                            label="PLZ"
                            value={form.zipCode}
                            size={InputSize.normal}
                            errorMessage={errors["ZipCode"]}
                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "zipCode")} />
                        <TextBox
                            label="Ort"
                            value={form.city}
                            size={InputSize.normal}
                            spellCheck={true}
                            errorMessage={errors["City"]}
                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "city")} />
                    </FormGroup>
                    <FormGroup>
                        <CheckBox
                            label="Aktiv"
                            isChecked={form.isActive}
                            onCheckedChanged={this.onCheckboxChanged} />
                    </FormGroup>
                </ScrollViewer>
            </div>
        );
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        const {
            isLoading,
            mode,
            mortician,
            form,
            errors,
            alertMessage,
            alertType
        } = this.state;

        var icon: string = "loading";
        var title: string = "Daten werden geladen...";
        var buttonHeader: string = "";

        if (!isLoading)
        {
            if (mode === Mode.create)
            {
                icon = "plus";
                title = "Neuen Bestatter erstellen";
                buttonHeader = "Erstellen";
            }
            else
            {
                icon = "pencil";
                title = `Bestatter "${mortician.fullname_without_title_rev}" bearbeiten`;
                buttonHeader = "Speichern";
            }
        }

        return (
            <Window
                isOpen={true}
                titleIcon={icon}
                title={title}
                onClosing={this.onClosing}
                isMaximized={false}
                width={600}
                height={560}
                onEnterKeyUp={this.onSaveOrCreateClick}
                onEscapeKeyUp={() => this.onClosing(false)}>
                <div className="settings_dialog">
                    <Alert
                        style={utils.getGridChildStyle(1, 1, 1, 1)}
                        alertType={alertType}
                        isVisible={alertMessage ? true : false}
                        isClosable={true}
                        onHide={() => this.setState({ alertMessage: null, alertType: null })}>
                        {alertMessage}
                    </Alert>
                    {isLoading ? <Loader loaderStyle={LoaderStyle.style1} /> : this.renderContent()}
                    <div className="settings_dialog_actions">
                        {
                            mode === Mode.edit &&
                            <Button
                                text="Löschen"
                                type={ButtonType.danger}
                                onClick={this.onDeleteClick} />
                        }
                        <div style={{ flexGrow: 1 }}></div>
                        <Button
                            text={buttonHeader}
                            type={ButtonType.primary}
                            isDisabled={this._isSaveButtonDisabled}
                            onClick={this.onSaveOrCreateClick} />
                        <Button
                            text="Abbrechen"
                            type={ButtonType.default}
                            onClick={() => this.onClosing(false)} />
                    </div>
                </div>
            </Window>
        );
    }
}